import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Trans } from 'services/i18n';

import {
  selectIsDataFeedRateLimitingFeatureFlagOn,
  selectUserProject,
} from 'core/userContext/selectors';
import { Button, BUTTON_VARIANTS, GhostSuccessButton, BUTTON_SIZES } from 'ui/button';
import { InputText } from 'ui/inputs/inputText';
import { Add16 } from 'ui/icons/action/Add16';
import { Modal, ModalFooter, ModalHeader, MODAL_SIZES, MODAL_VARIANTS } from 'ui/modal';
import { RadioList } from 'ui/radioList';
import { ALIGNMENT } from 'ui/menu/constants';
import { BasicSelect } from 'ui/selects/BasicSelect';
import { DataFeedsModals, DataFeedsModalStateType, useModalContext } from 'utils/contexts/modal';
import { getUnique } from 'utils/unique';

import HTTPHeaderInput from './HTTPHeaderInput';
import { useTranslations } from '../../hooks/useTranslations';
import { addDefaultId, getRateLimitPerSec, validateAlias } from '../../utils';
import {
  Code,
  CustomHeaders,
  LabelSecondaryText,
  RateLimit,
  RateLimitInput,
  RateLimitText,
  StyledFormField,
  StyledModalBody,
} from './DataFeedsModal.styled';
import { clearDataFeedsError, updateDataFeedsRequest } from '../../actions';
import { getError } from '../../selectors';
import { CustomHttpHeaders, DataFeed, FEED_FORMATS, NewDataFeed } from '../../types';

const OptionalFormFieldLabel = (label: string) => {
  const { t } = useTranslations();

  return (
    <>
      <span>{label}</span>
      <LabelSecondaryText>&nbsp;({t('label.optional')})</LabelSecondaryText>
    </>
  );
};

const formatOptions = Object.values(FEED_FORMATS).map((f) => ({
  id: f,
  label: f,
  value: f,
}));

export const ids = {
  btnSubmit: 'df-edit-modal-submit-btn',
  inputAlias: 'df-edit-modal-alias-input',
  inputFormat: 'df-edit-modal-format-input',
  inputName: 'df-edit-modal-name-input',
  inputRateLimit: 'df-edit-modal-rate-limit-input',
  inputToken: 'df-edit-modal-token-input',
  inputUrl: 'df-edit-modal-url-input',
  modal: 'df-edit-modal',
  optRateLimitOptCustom: 'df-edit-modal-rate-limit-opt-custom',
  optRateLimitOptDefault: 'df-edit-modal-rate-limit-opt-default',
};

export const testIds = {
  btnAddHeader: 'df-edit-modal-add-header-button',
  btnSubmit: ids.btnSubmit,
  inputAlias: ids.inputAlias,
  inputFormat: ids.inputFormat,
  inputName: ids.inputName,
  inputRateLimit: ids.inputRateLimit,
  inputToken: ids.inputToken,
  inputUrl: ids.inputUrl,
};

export const RATE_LIMIT_DEFAULT = 'none';
export const RATE_LIMIT_CUSTOM = 'custom';
export const rateLimitOptions = () => {
  const { t } = useTranslations();
  return [
    {
      id: ids.optRateLimitOptDefault,
      description: t('input.rateLimit.option.none.description'),
      name: t('input.rateLimit.option.none.label'),
      value: RATE_LIMIT_DEFAULT,
    },
    {
      id: ids.optRateLimitOptCustom,
      description: t('input.rateLimit.option.custom.description'),
      name: t('input.rateLimit.option.custom.label'),
      value: RATE_LIMIT_CUSTOM,
    },
  ];
};

export const DataFeedsModal = () => {
  const { t } = useTranslations();
  const dispatch = useDispatch();
  const activeProject = useSelector(selectUserProject);
  const errorResponse = useSelector(getError);
  const { modalState, hideModal } = useModalContext<DataFeedsModalStateType>();
  const isNewFeed = modalState?.modal === DataFeedsModals.CreateFeed;
  const dataFeed = isNewFeed
    ? (modalState?.dataFeed as NewDataFeed)
    : (modalState?.dataFeed as DataFeed);
  const allowRateLimiting = useSelector(selectIsDataFeedRateLimitingFeatureFlagOn);

  /**
   * -- State --
   * name: user-assigned name
   * url: url of feed
   * format: data format of feed
   * alias: handlebars alias (optional)
   * authToken: authentication token for feed request (optional)
   * headers: modifiable HTTP headers (optional)
   * hasCustomRateLimit: whether rate limit is default, or custom
   * rateLimitPerSec: rate limit, in messages per second
   * isRateLimitTouched: whether to show rate limit error
   *
   */
  const [name, setName] = useState<string>(dataFeed.name);
  const [url, setUrl] = useState<string>(dataFeed.url);
  const [format, setFormat] = useState<FEED_FORMATS>(dataFeed.format);
  const [alias, setAlias] = useState<string>(dataFeed.alias || '');
  const [authToken, setAuthToken] = useState<string>(dataFeed.authToken || '');
  const [headers, setHeaders] = useState<CustomHttpHeaders>(
    addDefaultId(dataFeed.customHttpHeaders),
  );
  const [hasCustomRateLimit, setHasCustomRateLimit] = useState(!!dataFeed.rateLimitPerMin);
  const [rateLimitPerSec, setRateLimitPerSec] = useState<string>(
    getRateLimitPerSec(dataFeed.rateLimitPerMin),
  );
  const [isRateLimitTouched, touchRateLimit] = useState(false);
  const [showErrors, toggleErrors] = useState(!!errorResponse);

  // The modal can only be submitted with required fields and valid key/value header pairs
  const areHeadersInvalid = () =>
    Object.values(headers).some((header) => !header.name || !header.value);
  const isModalValidated = name && url && !areHeadersInvalid();

  /**
   * -- Event Handlers --
   * addHeader: add a header to the list
   * setRateLimit: update rate limit, but updated touched
   * toggleRateLimit: set rate limit to default/custom, on radio select
   * submitForm: submit the modal
   */
  const addHeader = (id: string): void => {
    setHeaders({
      ...headers,
      [id]: {
        id,
        name: '',
        value: '',
      },
    });
  };

  const setRateLimit = (value: string) => {
    if (!isRateLimitTouched) touchRateLimit(true);
    setRateLimitPerSec(value.replace(/[^0-9]/g, '')); // integers only
  };

  const toggleRateLimit = (value: string) =>
    value === RATE_LIMIT_CUSTOM ? setHasCustomRateLimit(true) : setHasCustomRateLimit(false);

  const primaryAction = (df: DataFeed | NewDataFeed) => {
    if (activeProject) {
      const updateDataFeedsAction = updateDataFeedsRequest(
        {
          ...df,
          projectId: activeProject.id, // the API requires a project ID to create a feed
        },
        hideModal,
      );
      dispatch(updateDataFeedsAction);
    }
  };

  const submitForm = () => {
    if (isModalValidated) {
      // strip the extra data off the headers before hitting the API
      const cleanHeaders = Object.values(headers).map((h) => ({
        name: h.name,
        value: h.value,
      }));
      const rateLimitPerMin =
        hasCustomRateLimit && Boolean(rateLimitPerSec) ? Number(rateLimitPerSec) * 60 : null;
      const formFields = { alias, authToken, format, name, rateLimitPerMin, url };
      // Rate limit is stored as "per minutes" on the backend, but must be shown as "per seconds" on the frontend.
      // Null indicates the default state of no custom rate limit.
      primaryAction({
        ...dataFeed,
        ...formFields,
        customHttpHeaders: cleanHeaders,
      });
    }
    toggleErrors(true);
  };

  /**
   * Validations
   * isRateLimitValid: whether rate limit is within valid bounds
   * errors: a missing URL or one not-validated by the API will show the URL error
   * isSubmitDisabled: submittable or no
   * rateLimit: currently selected radio value: default/custom
   */
  const isRateLimitInvalid = (rateLimit = '') =>
    rateLimit === '' || !/^\d+$/.test(rateLimit) || Number(rateLimit) <= 1;

  const errors = {
    alias: Boolean(alias?.length) && !validateAlias(alias || ''),
    format: showErrors && !format,
    name: showErrors && !name,
    rateLimit: hasCustomRateLimit && isRateLimitTouched && isRateLimitInvalid(rateLimitPerSec),
    url: showErrors && !!errorResponse,
  };

  const isSubmitDisabled =
    errors.alias || !format || !name || !url || areHeadersInvalid() || errors.rateLimit;

  const rateLimit = hasCustomRateLimit ? RATE_LIMIT_CUSTOM : RATE_LIMIT_DEFAULT;

  return (
    <Modal
      id={ids.modal}
      isModalOpen={true}
      onClose={() => {
        hideModal();
        dispatch(clearDataFeedsError());
      }}
      size={MODAL_SIZES.MEDIUM}
      variant={MODAL_VARIANTS.DEFAULT}
    >
      <ModalHeader>{t(`header.${isNewFeed ? 'newDataFeed' : 'editDataFeed'}`)}</ModalHeader>
      <StyledModalBody>
        <section>
          <StyledFormField
            errorText={t('input.name.errorText')}
            hasError={errors.name}
            helpText={t('input.name.helpText')}
            inputId={ids.inputName}
            labelText={t('input.name.label')}
          >
            <InputText
              data-test={testIds.inputName}
              id={ids.inputName}
              hasError={errors.name}
              onChange={setName}
              value={name}
            />
          </StyledFormField>
        </section>
        {isNewFeed && (
          <section>
            <StyledFormField
              descriptionText={t('input.alias.description')}
              errorText={t('input.alias.errorText')}
              hasError={errors.alias}
              helpText={t('input.alias.helpText')}
              inputId={ids.inputAlias}
              labelText={OptionalFormFieldLabel(t('input.alias.label'))}
            >
              <InputText
                data-test={testIds.inputAlias}
                id={ids.inputAlias}
                onChange={setAlias}
                placeholder={t('input.alias.placeholder')}
                value={alias}
              />
            </StyledFormField>
          </section>
        )}
        <section>
          <StyledFormField
            errorText={errorResponse || t('input.url.errorText')}
            hasError={errors.url}
            helpText={
              <Trans
                t={t}
                i18nKey="input.url.helpText"
                components={{
                  helpCode: <Code />,
                }}
                values={{ codeSample: '{{#urlEncode}}{{myParam}}{{/urlEncode}}' }}
              >
                Use <Code>{`{{#urlEncode}}{{myParam}}{{/urlEncode}}`}</Code> to encode parameters
              </Trans>
            }
            inputId={ids.inputUrl}
            labelText={t('input.url.label')}
          >
            <InputText
              data-test={testIds.inputUrl}
              id={ids.inputUrl}
              hasError={errors.url}
              onChange={setUrl}
              value={url}
            />
          </StyledFormField>
          <StyledFormField
            errorText={t('input.format.errorText')}
            hasError={errors.format}
            helpText={t('input.format.helpText')}
            inputId={ids.inputFormat}
            labelText={t('input.format.label')}
          >
            <BasicSelect
              data-test={testIds.inputFormat}
              defaultValues={[format]}
              horizontalAlignment={ALIGNMENT.RIGHT}
              id={ids.inputFormat}
              isError={errors.format}
              onSelect={([selectedFormat]) => setFormat(selectedFormat)}
              options={formatOptions}
            />
          </StyledFormField>
        </section>
        <section>
          <StyledFormField
            inputId={ids.inputToken}
            labelText={OptionalFormFieldLabel(t('input.token.label'))}
            descriptionText={t('input.token.description')}
          >
            <InputText
              data-test={testIds.inputToken}
              id={ids.inputToken}
              onChange={setAuthToken}
              value={authToken}
            />
          </StyledFormField>
        </section>
        <CustomHeaders>
          <section>
            <StyledFormField
              labelText={OptionalFormFieldLabel(t('input.headers.label'))}
              descriptionText={t('input.headers.description')}
            >
              <HTTPHeaderInput headers={headers} onChange={setHeaders} showErrors={showErrors} />
            </StyledFormField>
          </section>
          <section>
            <GhostSuccessButton
              data-test={testIds.btnAddHeader}
              onClick={() => addHeader(getUnique(12))}
              size={BUTTON_SIZES.SMALL}
            >
              <Add16 />
              <span>{t('button.addHttpHeader')}</span>
            </GhostSuccessButton>
          </section>
        </CustomHeaders>
        {allowRateLimiting && (
          <section>
            <StyledFormField
              errorText={t('input.rateLimit.errorText')}
              hasError={errors.rateLimit}
              inputId={ids.inputRateLimit}
              labelText={t('input.rateLimit.label')}
            >
              <RadioList
                name="rate-limit"
                options={rateLimitOptions()}
                onSelect={toggleRateLimit}
                value={rateLimit}
              />
              <RateLimit onClick={() => setHasCustomRateLimit(true)}>
                <RateLimitInput
                  data-test={testIds.inputRateLimit}
                  hasError={errors.rateLimit}
                  id={ids.inputRateLimit}
                  isDisabled={!hasCustomRateLimit}
                  onChange={setRateLimit}
                  // don's specify type so that we can filter decimal places
                  value={rateLimitPerSec || ''}
                ></RateLimitInput>
                <RateLimitText>messages per second</RateLimitText>
              </RateLimit>
            </StyledFormField>
          </section>
        )}
      </StyledModalBody>
      <ModalFooter cancelLabel={t('button.cancel')} size={MODAL_SIZES.MEDIUM}>
        <Button
          data-test={testIds.btnSubmit}
          id={ids.btnSubmit}
          isDisabled={isSubmitDisabled}
          onClick={submitForm}
          size={BUTTON_SIZES.LARGE}
          type="button"
          variant={BUTTON_VARIANTS.PRIMARY}
        >
          <span>{t(`button.${isNewFeed ? 'createFeed' : 'save'}`)}</span>
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default DataFeedsModal;
